import React, { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { useMessageMediaAccessQuery } from './useMessageMediaAccessQuery';
import { useTranslation } from 'react-i18next';

import Skeleton from '@mui/material/Skeleton';
import MicIcon from '@mui/icons-material/Mic';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';


const blink = keyframes`
  from { opacity: 1; }
  to { opacity: 0.2; }
`;

// animated MicIcon component
const BlinkingMic = styled(MicIcon)({
  animation: `${blink} 0.8s linear infinite alternate`,
});

const AudioMessageContent = ({ media, messageUuid }) => {
  const { t } = useTranslation();
  const [showTranscription, setShowTranscription] = useState(false);
  const shouldFetchMedia = Boolean(messageUuid) && media?.status === 'ready';

  const { data, isLoading, isError } = useMessageMediaAccessQuery(messageUuid, shouldFetchMedia);
  const audioUrl = data?.url ?? media?.signedUrl;
  const transcription = media?.transcription;
  const hasTranscription = transcription?.status === 'ready' && Boolean(transcription.text);

  const renderPending = () => {
    return (
      <BlinkingMic sx={{ color: 'text.secondary' }} />
    );
  }

  const renderLoading = () => {
    return (
      <Skeleton
        height={54}
        variant="rounded"
        sx={{
          borderRadius: '24px',
        }} />
    );
  }

  const renderError = () => {
    return (
      <Typography variant="body2">
        {t('messages.media.audio.error')}
      </Typography>
    );
  }


  if (media?.status === 'failed') {
    return renderError();
  }

  // Anything other than 'ready' (null, 'pending', 'processing') means the
  // media service hasn't finished yet — keep the skeleton until it has.
  // TODO: we might want to differentiate between pending and loading in the
  // future (see renderPending).
  if (media?.status !== 'ready' || isLoading) {
    return renderLoading();
  }

  if (isError) {
    return renderError();
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {audioUrl ? (
        <audio controls preload="none" src={audioUrl} style={{ display: 'block', width: '100%' }}>
          {t('messages.media.audio.unsupported')}
        </audio>
      ) : (
        <Typography variant="body2">
          {media?.filename || (media?.isVoiceMessage ? t('messages.media.audio.voiceMessage') : t('messages.media.audio.audio'))}
        </Typography>
      )}
      {hasTranscription && (
        <>
          <Button
            size="small"
            color="inherit"
            disableRipple
            onClick={() => setShowTranscription((shown) => !shown)}
          >
            {t(`messages.media.audio.transcription.${showTranscription ? 'hide' : 'show'}`)}
          </Button>
          <Collapse in={showTranscription} timeout="auto" unmountOnExit>
            <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
              {t('messages.media.audio.transcription.label')}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {transcription.text}
            </Typography>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default AudioMessageContent;
